import { flightMotion } from './flight-steering';
import * as THREE from 'three';
import RAPIER from '@dimforge/rapier3d-compat';
import { APRON_Y, airframe, type AirportLayout } from '../world/airport';
import { MATERIALS, type City, type Place } from '../world/city';
import type { Vec } from '../destruction/structure';
import type { Cluster, PhysicsCity } from '../physics/world';
import type { Person, Population } from './population';
import { Reservations } from './reservations';
import { AirportGround } from './airport-ground';
import { planeImpact } from './plane-impact';
import { aircraftWaterContact } from './aircraft-water';
import { AirManifest } from './air-manifest';

export type AircraftState =
  | 'turnaround'
  | 'pushback'
  | 'taxi-out'
  | 'takeoff'
  | 'excursion'
  | 'approach'
  | 'landing'
  | 'taxi-in'
  | 'holding'
  | 'test approach'
  | 'wreck';
export interface FlightLeg {
  point: THREE.Vector3;
  speed: number;
  state: AircraftState;
  reverse?: boolean;
}
export interface Aircraft {
  anchor: THREE.Vector3;
  id: string;
  cluster: Cluster;
  p: THREE.Vector3;
  q: THREE.Quaternion;
  velocity: THREE.Vector3;
  state: AircraftState;
  route: FlightLeg[];
  wait: number;
  stand: number;
  originalCells: number;
  flights: number;
  test: boolean;
  reason: string;
  runway: boolean;
  radius: number;
  impact?: Vec;
  mission?: { owner: string; faction: 'friendly' | 'hostile' };
  pilots?: string[];
}
interface Passenger {
  person: Person;
  gate: Place;
  state: 'waiting' | 'boarding' | 'aboard' | 'alighting';
  aircraft?: Aircraft;
  route: number[];
  since: number;
  crossing: number;
}
const v = (x: number, y: number, z: number) => new THREE.Vector3(x, y, z);
const anchor = v(17.5, 0, 13.75);
/** Two finite local excursion services. Every passenger and airframe keeps its identity. */
export class Aviation {
  // Trial is enabled only by explicit diagnostics until recovery is verified.
  cargoImpactEnabled = false;
  aircraft: Aircraft[] = [];
  passengers = new Map<string, Passenger>();
  log: { time: number; aircraft?: string; person?: string; action: string }[] = [];
  readonly reservations: Reservations;
  readonly layout: AirportLayout;
  readonly ground: AirportGround;
  readonly troops: AirManifest;
  time = 0;
  boardings = 0;
  arrivals = 0;
  impacts = 0;
  private epoch = 0;
  private serial = 900000;
  private runwayCheck = { time: -100, open: true };
  constructor(
    readonly city: City,
    readonly physics: PhysicsCity,
    readonly population: Population,
  ) {
    this.layout = city.airport!;
    this.reservations = new Reservations(() => physics.life.epoch);
    this.ground = new AirportGround(city, physics, population, () => this.aircraft.filter((a) => !a.mission));
    this.troops = new AirManifest(this);
    this.reset();
  }
  private record(action: string, a?: Aircraft, p?: Person): void {
    if (a && (action.startsWith('Departing') || action.startsWith('Returned to stand')))
      this.population.audio.play('door', 0.5, a.p);
    this.log.push({ time: this.time, aircraft: a?.id, person: p?.id, action });
    if (this.log.length > 512) this.log.shift();
  }
  clear(): void {
    this.troops.clear();
    this.ground.clear();
    for (const a of this.aircraft) {
      this.population.fire.unregister(a.id);
      this.physics.unregisterRuntimeStructure(a.cluster.building.id);
    }
    for (const t of this.passengers.values()) {
      t.person.hidden = false;
      t.person.controlled = false;
    }
    this.aircraft = [];
    this.passengers.clear();
    this.reservations.clear();
    this.log = [];
    this.time = this.boardings = this.arrivals = this.impacts = 0;
    this.serial = 900000;
    this.runwayCheck = { time: -100, open: true };
  }
  reset(): void {
    this.clear();
    this.epoch = this.physics.life.epoch;
    for (let i = 0; i < 2; i++) {
      const a = this.spawn(
        i ? 'twin-prop' : 'regional-jet',
        v(this.layout.stands[i].x, APRON_Y + 0.035, this.layout.stands[i].z),
        false,
      );
      a.stand = i;
      a.wait = 160 + i * 100;
      a.reason = 'Coastal excursion · check-in, boarding and ground turnaround';
    }
    this.ground.reset();
    // Residents leave real homes to reach a leased gate; no terminal-only decorative crowd.
    const residents = [...(this.population.civil?.routines.values() ?? [])]
      .filter((r) => r.home && r.role === 'resident')
      .sort(
        (a, b) =>
          Math.hypot(a.person.p.x - 385, a.person.p.z + 390) -
          Math.hypot(b.person.p.x - 385, b.person.p.z + 390),
      )
      .slice(0, 8);
    for (const r of residents) {
      r.role = 'traveller';
      r.phase = 0;
    }
  }
  private spawn(
    family: 'regional-jet' | 'twin-prop' | 'cargo-transport',
    point: THREE.Vector3,
    test: boolean,
    mission?: Aircraft['mission'],
  ): Aircraft {
    const b = airframe(this.serial++, family, !!mission);
    if (mission) {
      b.name = mission.owner.includes(':elite:')
        ? 'Blackguard ready air transport'
        : family === 'regional-jet'
          ? 'Unoccupied strike jet'
          : `${mission.faction === 'friendly' ? 'Garrison' : 'Hostile'} cargo transport`;
      for (const cell of b.cells) {
        const color = new THREE.Color(cell.color);
        if (Math.max(color.r, color.g, color.b) > 0.3 && cell.material !== 'glass')
          cell.color = mission.owner.includes(':elite:')
            ? 0x2b3033
            : mission.faction === 'friendly'
              ? 0x718262
              : 0x8b9295;
      }
    }
    const cluster = this.physics.registerRuntimeStructure(b);
    cluster.body.setBodyType(RAPIER.RigidBodyType.KinematicPositionBased, true);
    cluster.body.enableCcd(true);
    const a: Aircraft = {
      anchor: family === 'cargo-transport' ? v(19.375, 0, 19.375) : anchor,
      id: `aircraft:${b.id}`,
      cluster,
      p: point.clone(),
      q: new THREE.Quaternion(),
      velocity: new THREE.Vector3(),
      state: test ? 'test approach' : 'turnaround',
      route: [],
      wait: 0,
      stand: 0,
      originalCells: b.cells.length,
      flights: 0,
      test,
      reason: 'Ground turnaround',
      runway: false,
      radius: cluster.radius,
      mission,
    };
    this.aircraft.push(a);
    this.position(a, true);
    let heatTime = 0,
      fireAnchor = b.cells.find((c) => c.y === 1 && c.x === 9)?.id ?? b.cells[0].id;
    this.population.fire.register({
      id: a.id,
      category: 'vehicle',
      burnSeconds: 140,
      position: () => {
        let p = this.physics.cellPosition(fireAnchor);
        if (!p) {
          const replacement = this.physics.buildingCells(b.id)[0];
          if (replacement) {
            fireAnchor = replacement.id;
            p = this.physics.cellPosition(fireAnchor);
          }
        }
        return p ?? a.p;
      },
      fuel: () => (this.physics.buildingCells(b.id).length ? 1 : 0),
      onHeat: (heat, dt) => {
        heatTime += dt;
        if (heat > 0.82 && heatTime > 1) {
          heatTime = 0;
          if (a.state !== 'wreck') this.wreck(a, a.p, 'Aircraft fire · controlled flight lost');
          else this.physics.damageBuilding(b.id, a.p, 8, heat * 0.3);
        }
      },
    });
    return a;
  }
  /** Mission flights use the same live airframe, sweeps, fire and wreck outcome
   * as passenger flights. The event owns its finite crew and route decisions. */
  createMission(
    owner: string,
    faction: 'friendly' | 'hostile',
    point: Vec,
    heading: Vec,
    family: 'regional-jet' | 'cargo-transport' = 'cargo-transport',
  ): Aircraft {
    const aircraft = this.spawn(family, v(point.x, point.y, point.z), false, { owner, faction });
    aircraft.state = 'holding';
    aircraft.reason = 'Original mission aircraft awaiting its finite route';
    aircraft.q.setFromUnitVectors(v(0, 0, -1), v(heading.x, heading.y, heading.z).normalize());
    this.position(aircraft, true);
    return aircraft;
  }
  routeMission(aircraft: Aircraft, route: FlightLeg[], reason: string): boolean {
    if (!aircraft.mission || aircraft.state === 'wreck') return false;
    if (!this.hasPilot(aircraft)) return false;
    aircraft.route = route;
    aircraft.reason = reason;
    return true;
  }
  private hasPilot(a: Aircraft): boolean {
    return (
      !a.pilots ||
      a.pilots.some((id) => {
        const o = this.troops.people.get(id);
        return (
          o?.aircraft === a &&
          o.state === 'aboard' &&
          !o.soldier.person.fatal &&
          ['wait', 'operating aircraft'].includes(o.soldier.person.state)
        );
      })
    );
  }
  retireMission(aircraft: Aircraft): boolean {
    if (!aircraft.mission || aircraft.state === 'wreck' || this.troops.occupied(aircraft)) return false;
    if (
      !this.aircraft.includes(aircraft) ||
      aircraft.cluster.cells.size !== aircraft.originalCells ||
      [...aircraft.cluster.cells.values()].some((c) => c.hp < MATERIALS[c.material].strength)
    )
      return false;
    this.troops.forget(aircraft);
    this.population.fire.unregister(aircraft.id);
    this.physics.unregisterRuntimeStructure(aircraft.cluster.building.id);
    this.reservations.release(aircraft.id);
    this.aircraft = this.aircraft.filter((a) => a !== aircraft);
    return true;
  }
  private position(a: Aircraft, initial = false): void {
    const p = a.cluster.origin.clone().sub(a.anchor).applyQuaternion(a.q).add(a.p);
    if (initial) {
      a.cluster.body.setTranslation(p, true);
      a.cluster.body.setRotation(a.q, true);
      a.cluster.previous.copy(p);
      a.cluster.previousQ.copy(a.q);
    }
    a.cluster.body.setNextKinematicTranslation(p);
    a.cluster.body.setNextKinematicRotation(a.q);
    a.cluster.building.x = a.p.x - a.anchor.x;
    a.cluster.building.z = a.p.z - a.anchor.z;
    if (initial) this.physics.world.propagateModifiedBodyPositionsToColliders();
  }
  runwayOpen(): boolean {
    if (this.time - this.runwayCheck.time < 0.5) return this.runwayCheck.open;
    const open = this.layout.runway.supports.every((id) => {
      const owner = this.physics.cellOwner.get(id);
      return (
        owner !== undefined &&
        !!(this.physics.clusters.get(owner) ?? this.physics.dormant.get(owner))?.anchored
      );
    });
    this.runwayCheck = { time: this.time, open };
    return open;
  }
  waitAtGate(person: Person, gate: Place): boolean {
    if (this.passengers.has(person.id)) return true;
    if (
      !this.population.civil!.network.available(gate) ||
      person.p.distanceTo(v(gate.x, gate.y, gate.z)) > 0.45
    )
      return false;
    this.passengers.set(person.id, {
      person,
      gate,
      state: 'waiting',
      route: [],
      since: this.time,
      crossing: 0,
    });
    this.record('Checked in at reserved terminal gate', undefined, person);
    return true;
  }
  launchTest(point: THREE.Vector3, direction: THREE.Vector3): boolean {
    if (this.aircraft.filter((a) => a.test && a.state !== 'wreck').length >= 2) return false;
    const dir = direction.clone().setY(0).normalize();
    if (dir.lengthSq() < 0.5) dir.set(-1, 0, 0);
    const start = point
      .clone()
      .addScaledVector(dir, -700)
      .add(v(0, 180, 0));
    const a = this.spawn(this.cargoImpactEnabled ? 'cargo-transport' : 'regional-jet', start, true);
    a.q.setFromUnitVectors(v(0, 0, -1), point.clone().sub(start).normalize());
    this.position(a, true);
    a.route = [
      {
        point: point
          .clone()
          .addScaledVector(dir, 80)
          .add(v(0, -22, 0)),
        speed: 65,
        state: 'test approach',
      },
    ];
    a.reason = 'Unoccupied cargo transport · committed visible approach';
    this.record(a.reason, a);
    return true;
  }
  private depart(a: Aircraft): void {
    const s = this.layout.stands[a.stand],
      y = APRON_Y + 0.035,
      taxiX = s.x + (a.stand ? 35 : -35);
    const leg = (
      x: number,
      Y: number,
      z: number,
      speed: number,
      state: AircraftState,
      reverse = false,
    ): FlightLeg => ({
      point: v(x, Y, z),
      speed,
      state,
      reverse,
    });
    a.route = [
      leg(s.x, y, -356, 3, 'pushback', true),
      leg(taxiX, y, -356, 4, 'taxi-out'),
      leg(taxiX, y, -504, 6, 'taxi-out'),
      leg(450, y, -504, 6, 'taxi-out'),
      leg(450, y, -580, 6, 'taxi-out'),
      leg(700, y, -580, 20, 'takeoff'),
      leg(1120, 36, -580, 44, 'takeoff'),
      leg(1550, 120, -580, 52, 'excursion'),
      leg(1750, 160, -100, 52, 'excursion'),
      leg(1700, 160, 450, 52, 'excursion'),
      leg(1200, 160, 700, 52, 'excursion'),
      leg(1450, 160, 100, 52, 'excursion'),
      leg(1450, 60, -580, 42, 'approach'),
      leg(1120, 2.5, -580, 30, 'landing'),
      leg(1050, y, -580, 24, 'landing'),
      leg(450, y, -580, 16, 'landing'),
      leg(450, y, -504, 6, 'taxi-in'),
      leg(taxiX, y, -504, 6, 'taxi-in'),
      leg(taxiX, y, -356, 5, 'taxi-in'),
      leg(s.x, y, -356, 4, 'taxi-in'),
      leg(s.x, y, s.z, 3, 'taxi-in'),
    ];
    a.state = 'pushback';
    a.reason = 'Shared runway reserved · pushing back for coastal excursion';
    a.runway = true;
    this.record('Departing after boarding', a);
  }
  private wreck(a: Aircraft, point: Vec, reason: string): void {
    if (a.state === 'wreck') return;
    a.state = 'wreck';
    a.reason = reason;
    a.route = [];
    a.impact = { ...point };
    a.runway = false;
    this.impacts++;
    this.reservations.release(a.id);
    for (const c of this.physics.clusters.values())
      if (c.building.id === a.cluster.building.id) {
        c.anchored = false;
        c.body.setBodyType(RAPIER.RigidBodyType.Dynamic, true);
        c.body.enableCcd(true);
        c.body.setLinvel(a.velocity, true);
      }
    for (const ticket of this.passengers.values())
      if (ticket.aircraft === a) {
        const p = ticket.person;
        p.hidden = false;
        p.controlled = false;
        p.p.copy(a.p).add(v(0, 1.5, 0));
        p.previous.copy(p.p);
        p.velocity.copy(a.velocity);
        p.state = 'incapacitated';
        p.layer = 'aviation incident';
        this.passengers.delete(p.id);
      }
    const speed = a.velocity.length(),
      impact = planeImpact(speed, a.test && this.cargoImpactEnabled);
    // One primary impact only: wreck state was committed above. Distribute
    // primary damage over the wider kernel without multiplying heat as well.
    this.physics.blast(
      point,
      impact.radius,
      impact.energy,
      true,
      'impact',
      impact.heat / impact.damageScale,
      impact.damageScale,
    );
    if (speed > 18) this.population.fire.ignite(a.id);
    this.population.effects.burst(point, 0xd6ba8d, 60, Math.min(12, speed * 0.2));
    this.population.audio.play('blast', 1.7, point);
    this.record(reason, a);
  }
  update(dt: number): void {
    if (this.epoch !== this.physics.life.epoch) return;
    this.time += dt;
    this.ground.update(dt);
    for (const a of this.aircraft) {
      if (a.state === 'wreck') continue;
      if (!this.physics.clusters.has(a.cluster.id) || a.cluster.cells.size < a.originalCells) {
        this.wreck(a, a.p, 'Airframe damaged · controlled flight lost');
        continue;
      }
      if (a.pilots && !this.hasPilot(a)) {
        if (a.p.y > 5) this.wreck(a, a.p, 'Original flight operators unavailable');
        else {
          a.velocity.set(0, 0, 0);
          a.reason = 'Waiting for an original healthy flight operator';
        }
        continue;
      }
      if (!a.mission && a.state === 'turnaround') {
        a.wait -= dt;
        const unloading = [...this.passengers.values()].some(
          (t) => t.aircraft === a && t.state === 'alighting',
        );
        if (!unloading) {
          const boarding = [...this.passengers.values()].some(
            (t) => t.aircraft === a && t.state === 'boarding',
          );
          const count = [...this.passengers.values()].filter((t) => t.aircraft === a).length;
          const next = [...this.passengers.values()].find((t) => t.state === 'waiting');
          if (next && !boarding && count < 8 && a.wait > 3) {
            next.aircraft = a;
            next.state = 'boarding';
            next.since = this.time;
            next.person.controlled = true;
            next.person.state = 'boarding aircraft';
            next.person.detour = undefined;
            const n = this.population.civil!.network;
            next.route = n.route(
              n.nearest(next.person.p, next.person.layer),
              n.nearest(this.door(a), 'airport'),
              this.time,
            );
            if (!next.route.length) {
              next.state = 'waiting';
              next.aircraft = undefined;
              next.person.controlled = false;
            }
          }
        }
        const moving = [...this.passengers.values()].some(
          (t) => t.aircraft === a && ['boarding', 'alighting'].includes(t.state),
        );
        if (
          a.wait <= 0 &&
          !moving &&
          this.ground.ready(a) &&
          !this.ground.laneBusy &&
          this.runwayOpen() &&
          this.reservations.acquire('airport-runway', a.id, this.time, 1, 600)
        )
          this.depart(a);
        else if (a.wait <= 0)
          a.reason = !this.ground.ready(a)
            ? 'Waiting for completed servicing and original ground crews to clear the stand'
            : this.runwayOpen()
              ? 'Waiting for runway or passenger stair'
              : 'Runway damaged · departures suspended';
        continue;
      }
      if (a.runway) this.reservations.acquire('airport-runway', a.id, this.time, 1, 600);
      const leg = a.route[0];
      if (!leg) continue;
      if (
        !a.test &&
        !a.mission &&
        this.ground.laneBusy &&
        ['pushback', 'taxi-out', 'taxi-in'].includes(leg.state)
      ) {
        a.velocity.set(0, 0, 0);
        a.reason = 'Holding clear of the occupied apron service crossing';
        continue;
      }
      if (!a.test && !a.mission && !this.runwayOpen() && ['landing', 'approach'].includes(leg.state)) {
        a.route = [
          { point: v(1500, 170, -580), speed: 42, state: 'holding' },
          { point: v(1720, 170, -220), speed: 42, state: 'holding' },
          { point: v(1500, 170, -580), speed: 42, state: 'holding' },
        ];
        a.state = 'holding';
        a.reason = 'Damaged runway · finite flight holding for safe return';
      }
      const target = a.route[0],
        distance = target.point.distanceTo(a.p),
        move = flightMotion(a.p, a.q, target.point, target.speed, dt, target.reverse, a.test),
        step = move.length();
      a.state = target.state;
      a.velocity.copy(move).divideScalar(dt);
      if (!a.mission)
        a.reason = a.test
          ? 'Unoccupied test aircraft on visible collision approach'
          : `${a.state} · coastal excursion ${a.flights + 1}`;
      const solid = this.physics.sweepCluster(a.cluster, move),
        water = aircraftWaterContact(this.physics, this.city, a.cluster, move),
        hit = water && (!solid || water.time < solid.time) ? water : solid;
      if (hit) {
        a.p.addScaledVector(move, hit.time);
        this.position(a, true);
        this.wreck(
          a,
          hit.point,
          hit === water ? 'Airframe impact with the water surface' : 'Collision with live occupied geometry',
        );
        continue;
      }
      a.p.add(move);
      this.position(a);
      if (distance <= step + 0.001) {
        a.p.copy(target.point);
        a.route.shift();
        if (!a.route.length) {
          if (a.mission) {
            a.velocity.set(0, 0, 0);
            a.state = 'holding';
            a.flights++;
            this.record('Mission route completed at its physical destination', a);
            continue;
          }
          if (a.test) {
            this.wreck(a, a.p, 'Test aircraft ground impact');
            continue;
          }
          if (a.state === 'holding') {
            if (this.runwayOpen()) {
              a.route = [
                { point: v(1120, 2.5, -580), speed: 30, state: 'landing' },
                { point: v(1050, APRON_Y + 0.035, -580), speed: 24, state: 'landing' },
                { point: v(450, APRON_Y + 0.035, -580), speed: 16, state: 'landing' },
                { point: v(450, APRON_Y + 0.035, -504), speed: 6, state: 'taxi-in' },
                {
                  point: v(this.layout.stands[a.stand].x + (a.stand ? 35 : -35), APRON_Y + 0.035, -504),
                  speed: 6,
                  state: 'taxi-in',
                },
                {
                  point: v(this.layout.stands[a.stand].x + (a.stand ? 35 : -35), APRON_Y + 0.035, -356),
                  speed: 5,
                  state: 'taxi-in',
                },
                {
                  point: v(this.layout.stands[a.stand].x, APRON_Y + 0.035, -356),
                  speed: 4,
                  state: 'taxi-in',
                },
                {
                  point: v(this.layout.stands[a.stand].x, APRON_Y + 0.035, -412),
                  speed: 3,
                  state: 'taxi-in',
                },
              ];
            } else
              a.route = [
                { point: v(1720, 170, -220), speed: 42, state: 'holding' },
                { point: v(1500, 170, -580), speed: 42, state: 'holding' },
              ];
            continue;
          }
          a.state = 'turnaround';
          a.wait = 100;
          a.flights++;
          a.runway = false;
          a.velocity.set(0, 0, 0);
          a.q.identity();
          this.position(a, true);
          this.reservations.release(a.id);
          for (const t of this.passengers.values())
            if (t.aircraft === a && t.state === 'aboard') {
              t.state = 'alighting';
              t.since = this.time;
              t.crossing = 0;
              t.route = [];
            }
          this.record('Returned to stand · alighting and turnaround', a);
        }
      }
    }
    for (const ticket of [...this.passengers.values()]) this.stepPassenger(ticket, dt);
    this.troops.update(dt);
  }
  private door(a: Aircraft): THREE.Vector3 {
    return a.p.clone().add(v(-1.8, 1.475, -5));
  }
  private stepPassenger(t: Passenger, dt: number): void {
    const p = t.person,
      n = this.population.civil!.network;
    if (p.fatal || ['incapacitated', 'falling', 'flee'].includes(p.state)) {
      this.passengers.delete(p.id);
      p.controlled = false;
      p.hidden = false;
      return;
    }
    if (t.state === 'waiting') {
      if (
        !this.population.civil!.network.available(t.gate) ||
        !this.aircraft.some((a) => !a.test && !a.mission && a.state !== 'wreck')
      ) {
        this.passengers.delete(p.id);
        const r = this.population.civil!.routines.get(p.id);
        if (r) {
          r.role = 'resident';
          r.phase = 0;
          this.population.civil!.finishTransit(p, r.home ?? t.gate, 'Flight cancelled · returning home');
        }
      }
      return;
    }
    const a = t.aircraft!;
    p.previous.copy(p.p);
    if (t.state === 'aboard') {
      p.hidden = true;
      p.p.copy(a.p).add(v(0, 1.475, -5));
      return;
    }
    if (t.state === 'alighting') {
      if (
        t.crossing === 0 &&
        this.population.people.some(
          (other) => other !== p && !other.hidden && other.p.distanceTo(this.door(a)) < 0.9,
        )
      )
        return;
      const prior = [...this.passengers.values()].find(
        (ticket) =>
          ticket !== t &&
          ticket.aircraft === a &&
          ticket.state === 'alighting' &&
          ticket.crossing > 0 &&
          ticket.crossing < 2,
      );
      if (
        prior ||
        (t.crossing === 0 &&
          [...this.passengers.values()].some(
            (ticket) =>
              ticket !== t && ticket.aircraft === a && ticket.state === 'alighting' && ticket.route.length,
          ))
      )
        return;
      p.hidden = false;
      p.state = 'alighting aircraft';
      t.crossing += dt;
      if (t.crossing < 1.2) {
        p.p
          .copy(a.p)
          .add(v(-0.5, 1.475, -5))
          .lerp(this.door(a), Math.min(1, t.crossing / 1.2));
        return;
      }
      p.p.copy(this.door(a));
      p.layer = 'airport';
      p.hidden = false;
      p.controlled = false;
      this.passengers.delete(p.id);
      this.arrivals++;
      this.record('Alighted · walking home from terminal', a, p);
      const r = this.population.civil!.routines.get(p.id)!;
      r.phase = 1;
      this.population.civil!.finishTransit(
        p,
        r.home ?? t.gate,
        'Returned from coastal excursion · walking home',
      );
      return;
    }
    p.state = 'boarding aircraft';
    const next = t.route[0];
    if (next !== undefined) {
      const node = n.nodes[next];
      if (Math.hypot(p.p.x - node.x, p.p.z - node.z) < 0.13 && Math.abs(p.p.y - node.y) < 0.36) {
        t.route.shift();
        p.layer = node.layer;
        p.detour = undefined;
      } else if (this.population.footClear(p, node)) this.population.walkTo(p, node, dt, 1.65);
      if (this.time - t.since > 180) {
        t.state = 'waiting';
        t.aircraft = undefined;
        p.controlled = false;
        p.detour = undefined;
        this.population.civil!.finishTransit(
          p,
          t.gate,
          'Boarding access obstructed · returning to terminal gate',
        );
        this.passengers.delete(p.id);
      }
      return;
    }
    const door = this.door(a);
    if (p.p.distanceTo(door) > 0.18 && t.crossing === 0) {
      this.population.walkTo(p, door, dt, 1.2);
      return;
    }
    t.crossing += dt;
    p.p.copy(door).lerp(a.p.clone().add(v(-0.5, 1.475, -5)), Math.min(1, t.crossing / 1.2));
    if (t.crossing >= 1.2) {
      t.state = 'aboard';
      p.state = 'aboard coastal flight';
      p.hidden = true;
      this.population.civil!.reservations.release(p.id);
      this.boardings++;
      this.record('Boarded original aircraft', a, p);
    }
  }
  snapshot(): Record<string, number> {
    return {
      ...this.ground.snapshot(),
      aircraft: this.aircraft.length,
      activeAircraft: this.aircraft.filter((a) => a.state !== 'wreck').length,
      flightBoardings: this.boardings,
      flightArrivals: this.arrivals,
      aircraftImpacts: this.impacts,
      completedFlights: this.aircraft.reduce((n, a) => n + a.flights, 0),
    };
  }
  inspect(): Record<string, unknown> {
    return {
      ...this.snapshot(),
      runwayOpen: this.runwayOpen(),
      ground: this.ground.inspect(),
      troops: this.troops.inspect(),
      aircraft: this.aircraft.map((a) => ({
        id: a.id,
        building: a.cluster.building.id,
        name: a.cluster.building.name,
        originalCells: a.originalCells,
        cargoImpactEnabled: a.test && this.cargoImpactEnabled,
        position: a.p.toArray(),
        state: a.state,
        reason: a.reason,
        flights: a.flights,
        test: a.test,
        mission: a.mission,
        cells: a.cluster.cells.size,
        route: a.route.length,
        impact: a.impact,
      })),
      passengers: [...this.passengers.values()].map((t) => ({
        person: t.person.id,
        state: t.state,
        aircraft: t.aircraft?.id,
        gate: t.gate.id,
        route: t.route.length,
      })),
      log: this.log,
    };
  }
  dispose(): void {
    this.clear();
    this.ground.dispose();
  }
}
